/**
 * intersectFields — AK-368c best-effort детект пересечения импортируемого
 * контура с уже существующим полем (клиентски, БЕЗ @turf/boolean-intersects,
 * который не в зависимостях проекта).
 *
 * Founder-умолчание: пересечение = ⚠ warning, НЕ блокер. Помечаем строку в
 * списке «пересекается с <имя>», чекбокс остаётся выбранным. Поэтому точность
 * тут «достаточная» — ложно-отрицательный (не поймали пересечение) просто не
 * покажет метку, ложно-положительный маловероятен из-за двух проверок:
 *   1. bbox-кандидаты (дёшево отсекаем заведомо далёкие поля);
 *   2. настоящий тест: пересечение рёбер ИЛИ вложенность (точка одного кольца
 *      внутри другого — покрывает «одно поле целиком внутри другого»).
 *
 * Всё на внешних кольцах (дырки игнорируем — для «пересекается ли вообще» их
 * достаточно). Чистые функции без DOM/сети — покрыты юнит-тестом.
 */
import type { ImportGeometry } from './importApi';

/** [lng, lat]. */
export type LngLatPt = [number, number];
/** [minLng, minLat, maxLng, maxLat]. */
export type BBox = [number, number, number, number];

/** Существующее поле-кандидат: имя + все внешние кольца его геометрии. */
export interface ExistingFieldRings {
  id: string;
  name: string;
  rings: LngLatPt[][];
  bbox: BBox;
}

/** Внешние кольца из Polygon/MultiPolygon (дырки отбрасываем). */
export function outerRings(geometry: ImportGeometry | null): LngLatPt[][] {
  if (!geometry) return [];
  if (geometry.type === 'Polygon') {
    const poly = geometry.coordinates as number[][][];
    return poly.length ? [poly[0] as LngLatPt[]] : [];
  }
  // MultiPolygon: [ [ [outer], [hole]... ], ... ] → берём outer каждого полигона.
  const mp = geometry.coordinates as number[][][][];
  return mp.map((poly) => poly[0] as LngLatPt[]).filter((r) => r && r.length >= 3);
}

/** bbox набора колец. Пустой ввод → вырожденный bbox [0,0,0,0]. */
export function ringsBBox(rings: LngLatPt[][]): BBox {
  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;
  for (const ring of rings) {
    for (const [lng, lat] of ring) {
      if (lng < minLng) minLng = lng;
      if (lat < minLat) minLat = lat;
      if (lng > maxLng) maxLng = lng;
      if (lat > maxLat) maxLat = lat;
    }
  }
  if (!Number.isFinite(minLng)) return [0, 0, 0, 0];
  return [minLng, minLat, maxLng, maxLat];
}

/** Пересекаются ли bbox'ы (касание границей считаем пересечением). */
export function bboxOverlap(a: BBox, b: BBox): boolean {
  return a[0] <= b[2] && a[2] >= b[0] && a[1] <= b[3] && a[3] >= b[1];
}

/** Ориентация тройки точек: >0 ccw, <0 cw, 0 коллинеарны. */
function cross(o: LngLatPt, a: LngLatPt, b: LngLatPt): number {
  return (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
}

function onSegment(p: LngLatPt, q: LngLatPt, r: LngLatPt): boolean {
  return (
    Math.min(p[0], r[0]) <= q[0] &&
    q[0] <= Math.max(p[0], r[0]) &&
    Math.min(p[1], r[1]) <= q[1] &&
    q[1] <= Math.max(p[1], r[1])
  );
}

/** Пересекаются ли отрезки p1p2 и p3p4 (включая касание). */
export function segmentsIntersect(
  p1: LngLatPt,
  p2: LngLatPt,
  p3: LngLatPt,
  p4: LngLatPt,
): boolean {
  const d1 = cross(p3, p4, p1);
  const d2 = cross(p3, p4, p2);
  const d3 = cross(p1, p2, p3);
  const d4 = cross(p1, p2, p4);
  if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) {
    return true;
  }
  if (d1 === 0 && onSegment(p3, p1, p4)) return true;
  if (d2 === 0 && onSegment(p3, p2, p4)) return true;
  if (d3 === 0 && onSegment(p1, p3, p2)) return true;
  if (d4 === 0 && onSegment(p1, p4, p2)) return true;
  return false;
}

/** Точка внутри кольца (ray-casting, кольцо замкнутое или нет). */
export function pointInRing(pt: LngLatPt, ring: LngLatPt[]): boolean {
  let inside = false;
  const n = ring.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = ring[i][0];
    const yi = ring[i][1];
    const xj = ring[j][0];
    const yj = ring[j][1];
    const intersect =
      yi > pt[1] !== yj > pt[1] &&
      pt[0] < ((xj - xi) * (pt[1] - yi)) / (yj - yi + Number.EPSILON) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

/** Пересекаются/вкладываются ли два внешних кольца. */
export function ringsIntersect(a: LngLatPt[], b: LngLatPt[]): boolean {
  if (a.length < 3 || b.length < 3) return false;
  // 1. Пересечение рёбер (частичное наложение / касание границ).
  for (let i = 0; i < a.length - 1; i++) {
    for (let j = 0; j < b.length - 1; j++) {
      if (segmentsIntersect(a[i], a[i + 1], b[j], b[j + 1])) return true;
    }
  }
  // 2. Вложенность (одно кольцо целиком внутри другого — рёбра не пересекаются).
  if (pointInRing(a[0], b)) return true;
  if (pointInRing(b[0], a)) return true;
  return false;
}

/**
 * Первое существующее поле, с которым пересекается импорт-геометрия, или null.
 * bbox-префильтр отсекает далёкие кандидаты до дорогого ring-теста.
 */
export function firstIntersectingField(
  geometry: ImportGeometry | null,
  candidates: readonly ExistingFieldRings[],
): { id: string; name: string } | null {
  const importRings = outerRings(geometry);
  if (!importRings.length) return null;
  const importBBox = ringsBBox(importRings);
  for (const field of candidates) {
    if (!bboxOverlap(importBBox, field.bbox)) continue;
    for (const ir of importRings) {
      for (const fr of field.rings) {
        if (ringsIntersect(ir, fr)) {
          return { id: field.id, name: field.name };
        }
      }
    }
  }
  return null;
}
